import { Component, OnInit, OnDestroy } from '@angular/core';
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { of, Observable, forkJoin, Subscription } from "rxjs";
import { tap, switchMap } from 'rxjs/operators';
import { DatePipe } from '@angular/common';

import { BondingReportSearchCriteria, ReportSearchCriteria } from '../../../models/admin-reports.model';
import { DateService } from "../../../../common/services/date.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { FinancialAssuranceService } from "../../../../common/services/financial-assurance.service";
import { AdminReportsService } from '../../../services/admin-reports.service';

@Component({
    selector: 'app-collateral-released-report',
    templateUrl: './collateral-released-report.component.html',
    styleUrls: ['./collateral-released-report.component.scss'],
    standalone: false
})
export class CollateralReleasedReportComponent implements OnInit, OnDestroy {
	gridOptions: GridOptions;
	gridApi?: GridApi;
    reportData: any[] = []; // Replace with BondingReceivedListingModel[]
    subHeader: SubHeader = new SubHeader({
        title: "Collateral Released Report",
        buttons: []
    });
    subscription: Subscription = new Subscription();

    currentDate: Date = new Date();


    get invalidDateCombination(): boolean {
        let invalid: boolean = false;
        if (this.toDate != null && this.fromDate != null) {
            invalid = this.toDate < this.fromDate;
        }
        return invalid;
    }

    toDate: Date | null;
    fromDate: Date | null;

    get excelStyles(): any[] {
        if (this.formatService) return this.formatService.excelStyles;
        return [];
    }

    constructor(
        private datePipe: DatePipe, public reportService: AdminReportsService,
        private readonly dateService: DateService, private readonly formatService: GridFormatterService, private subHeaderService: SubHeaderService,

	) { }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        this.gridOptions = {
            columnDefs: this.createColumnDefinitions(),
            headerHeight: 50,
            onGridReady: (params) => {
				this.gridApi = params.api;
				this.gridApi.setGridOption("loading", true);
				this.generateReport();
			}
        };
        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = this.currentDate;
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    generateReport() {
        // Show loading overlay before starting the search
		if (this.gridApi) {
			this.gridApi.setGridOption("loading", true);
        }
        const criteria = new BondingReportSearchCriteria();
        criteria.startDate = this.fromDate;
        criteria.endDate = this.toDate;
        criteria.getReleased = true;

        this.subscription = this.reportService.getCollateralReleased(criteria).subscribe({
            next: (data) => {
                this.reportData = data;
                if (this.gridApi  != null) {
                    this.gridApi.setGridOption("rowData", this.reportData ?? []);
                    this.gridApi.setGridOption("loading", false);
                    this.gridApi.autoSizeAllColumns();
                }
            },
            error: (fail) => {
                console.error(fail);
                if (this.gridOptions && this.gridApi) {
                    this.gridApi.setGridOption("loading", false);
                }
            }
        });
    }

    createColumnDefinitions(): ColDef[] {
        return [
            {
                headerName: "Application Number",
                headerTooltip: "Application Number",
                field: "applicationNumber",
                filter: "agTextColumnFilter",
                minWidth: 130,
                maxWidth: 135,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            },
            {
                headerName: "Instrument Type (Type of Collateral)",
                headerTooltip: "Instrument Type (Type of Collateral)",
                field: "instrumentType",
                filter: "agTextColumnFilter",
                minWidth: 150,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            },
            {
                headerName: "Date Released",
                headerTooltip: "Date Released",
                field: "dateReceived",
                filter: "agDateColumnFilter",
                minWidth: 120,
                resizable: true,
                headerClass: "wrapped",
                sortable: true,
                valueFormatter: this.formatService.formatDate('dateReceived'),
                filterParams: this.formatService.defaultDateFilterParams
            },
            {
                headerName: "Amount",
                headerTooltip: "Amount",
                field: "amount",
                filter: "agNumberColumnFilter",
                filterParams: this.formatService.defaultNumberFilterParams,
                minWidth: 100,
                resizable: true,
                headerClass: "wrapped",
                sortable: true,
                valueFormatter: this.formatService.formatCurrency("amount", "USD", "symbol", "1.2-2"),
            },
            {
                headerName: "Instrument ID",
                headerTooltip: "Instrument ID",
                field: "instrumentId",
                filter: "agTextColumnFilter",
                minWidth: 135,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            },
            {
                headerName: "Owner Name",
                headerTooltip: "Owner Name",
                field: "ownerName",
                filter: "agTextColumnFilter",
                minWidth: 150,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            },
            {
                headerName: "RICS Org ID",
                headerTooltip: "RICS Org ID",
                field: "ricsOrgId",
                filter: "agTextColumnFilter",
                minWidth: 125,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            },
            {
                headerName: "RBDMS Owner Number",
                headerTooltip: "RBDMS Owner Number",
                field: "rbdmsOwnerNumber",
                filter: "agTextColumnFilter",
                minWidth: 120,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            },
            {
                headerName: "Well API Number",
                headerTooltip: "Well API Number",
                field: "wellApiNumber",
                filter: "agTextColumnFilter",
                minWidth: 125,
                resizable: true,
                headerClass: "wrapped",
                sortable: true
            }
        ];
    }

    downloadReport() {
        const criteria = new BondingReportSearchCriteria();
        criteria.startDate = this.fromDate;
        criteria.endDate = this.toDate;
        criteria.getReleased = true;
        let fileName = "CollateralReleasedReport";
        if (this.fromDate && this.toDate) {
            const fromDate = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
            const toDate = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
            fileName = `CollateralReleasedReport_${fromDate}_${toDate}`;
        }

        const sheetName = "Collateral Released Report";
        this.formatService.exportDataToExcel(fileName, sheetName, this.gridApi);
    }

    fromDateChanged(model: Date): void {
        this.fromDate = model;
    }

    toDateChanged(model: Date): void {
        if (model > this.currentDate) {
            this.toDate = this.currentDate;
        } else {
            this.toDate = model;
        }
    }

    resizeColumns(): void {
        if (this.gridApi) {
            this.gridApi.sizeColumnsToFit();
        }
    }

}
